import client from './client';
import type { Template } from '../types';

export type ReportedMessageStatus = 'pending' | 'approved' | 'rejected';

export interface ReportedMessage {
  id: number;
  reporter_email: string;
  subject: string;
  body_text: string;
  body_html: string;
  status: ReportedMessageStatus;
  converted_template_id: number;
  reviewed_by: string;
  created_at: string;
  reviewed_at: string;
  assigned_reviewer?: string;
  classification?: string;
  reason_code?: string;
  redacted_at?: string;
}

export interface ReportAudit { id: number; action: string; actor: string; created_at: string; note?: string; reason_code?: string }
export interface ReportOperations {
  pending_count: number; unassigned_count: number; oldest_pending_at?: string; oldest_pending_age_hours?: number;
  // These are in-process counters (see models/reported_message_admin.go), not
  // durable totals -- they reset to 0 on every server restart.
  intake_failures_since_start: number; review_failures_since_start: number;
  retention_redactions_since_start: number; authorized_deletions_since_start: number;
}

export interface ReportedMessageListParams {
  status?: ReportedMessageStatus;
  search?: string;
  created_after?: string; // RFC3339
  created_before?: string; // RFC3339
  page?: number;
  per_page?: number;
}

export interface ReportedMessageListResponse {
  data: ReportedMessage[];
  total: number;
  page: number;
  per_page: number;
}

export type ReportVerdict = 'simulation' | 'suspected-phish' | 'spam' | 'unknown';

export interface ReportAttachment {
  filename: string;
  sha256: string;
}

export interface ReportLookalike {
  domain: string;
  org_domain: string;
  reason: string;
}

export interface ReportAuthResults {
  dkim?: string;
  spf?: string;
  dmarc?: string;
  raw?: string;
}

export interface ReportAddress {
  address: string;
  domain?: string;
}

export interface ReportedAnalysisExtracted {
  message_id?: string;
  subject?: string;
  from?: ReportAddress[];
  reply_to?: ReportAddress[];
  return_path?: ReportAddress[];
  urls?: string[];
  url_domains?: string[];
  attachments?: ReportAttachment[];
  lookalikes?: ReportLookalike[];
  auth?: ReportAuthResults;
  rids?: string[];
  bulk_indicators?: string[];
}

export interface ReportedAnalysisRecord {
  id: number;
  reported_message_id: number;
  verdict: ReportVerdict | string;
  verdict_reasons: string[];
  cluster_key: string;
  extracted: ReportedAnalysisExtracted;
  created_at: string;
}

export interface ReportedMessageCluster {
  key: string;
  count: number;
  verdicts: Record<string, number>;
  first_seen: string;
  senders: string[];
}

export interface ReportIOCs {
  urls: string[];
  domains: string[];
  attachments: ReportAttachment[];
  lookalikes: ReportLookalike[];
  auth: ReportAuthResults;
}

export const reportedMessagesApi = {
  getAll: (params?: ReportedMessageListParams) =>
    client.get<ReportedMessageListResponse>('/reported-messages/', { params }),

  getById: (id: number) =>
    client.get<ReportedMessage>(`/reported-messages/${id}`),

  approve: (id: number, name: string, content: { subject: string; text: string; html: string; redactions_confirmed: boolean }) =>
    client.post<Template>(`/reported-messages/${id}/approve`, { name, ...content }),

  review: (id: number, fields: { assigned_reviewer: string; classification: string; reason_code: string }) => client.patch<ReportedMessage>(`/reported-messages/${id}/review`, fields),
  addNote: (id: number, note: string) => client.post(`/reported-messages/${id}/notes`, { note }),
  audit: (id: number) => client.get<{ data: ReportAudit[] }>(`/reported-messages/${id}/audit`),
  remove: (id: number) => client.delete(`/reported-messages/${id}`),
  operations: () => client.get<ReportOperations>('/reported-messages/operations'),
  applyRetention: () => client.post<{ redacted_count: number; cutoff: string }>('/reported-messages/retention/apply'),

  reject: (id: number) =>
    client.post<{ success: boolean; message: string }>(`/reported-messages/${id}/reject`),

  analysis: (id: number) =>
    client.get<ReportedAnalysisRecord>(`/reported-messages/${id}/analysis`),

  reanalyze: (id: number) =>
    client.post<ReportedAnalysisRecord>(`/reported-messages/${id}/reanalyze`),

  clusters: () =>
    client.get<{ data: ReportedMessageCluster[] }>('/reported-messages/clusters'),

  iocs: (id: number) =>
    client.get<ReportIOCs>(`/reported-messages/${id}/iocs`),
};
